import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { plansApi } from '../utils/api';

// Game card data
const featuredGames = [
  {
    id: 1,
    title: 'Balloon Pop',
    description: 'Pop colorful balloons by saying the words!',
    icon: '🎈',
    color: 'bg-gradient-to-br from-coral-400 to-orange-500',
    skills: ['Articulation', 'Volume Control'],
  },
  {
    id: 2,
    title: 'Rocket Launch',
    description: 'Count down and blast off to space!',
    icon: '🚀',
    color: 'bg-gradient-to-br from-primary-500 to-indigo-600',
    skills: ['Numbers', 'Sequencing'],
  },
  {
    id: 3,
    title: 'Fruit Slice',
    description: 'Slice fruits by naming them correctly!',
    icon: '🍎',
    color: 'bg-gradient-to-br from-teal-400 to-teal-600',
    skills: ['Vocabulary', 'Pronunciation'],
  },
  {
    id: 4,
    title: "Pip's Kitchen",
    description: 'Help Pip cook by requesting ingredients!',
    icon: '🍳',
    color: 'bg-gradient-to-br from-yellow-400 to-orange-400',
    skills: ['Requesting', 'Social Skills'],
  },
  {
    id: 5,
    title: 'Animal Chat',
    description: 'Talk to animals and learn their sounds!',
    icon: '🦁',
    color: 'bg-gradient-to-br from-purple-400 to-pink-500',
    skills: ['Sounds', 'Imitation'],
  },
  {
    id: 6,
    title: 'Speed Race',
    description: 'Race cars by speaking quickly and clearly!',
    icon: '🏎️',
    color: 'bg-gradient-to-br from-cyan-400 to-primary-500',
    skills: ['Fluency', 'Pacing'],
  },
];

// Therapist testimonials
const therapistTestimonials = [
  { name: 'Dr. Sarah Chen', title: 'Speech-Language Pathologist', emoji: '👩‍⚕️', quote: 'Play is the language of childhood. When we speak that language, children open up in ways traditional therapy can\'t achieve.' },
  { name: 'Dr. Michael Rodriguez', title: 'Clinical Psychologist', emoji: '👨‍⚕️', quote: 'I\'ve seen more progress in 10 minutes of engaged play than in hours of repetitive drills. SpeechFormation gets this.' },
  { name: 'Dr. Emily Watson', title: 'Pediatric SLP', emoji: '👩‍⚕️', quote: 'Children on the spectrum often experience therapy fatigue. By embedding goals into genuine play, we bypass resistance.' },
  { name: 'Dr. James Park', title: 'Behavioral Therapist', emoji: '👨‍⚕️', quote: 'The natural prompt fading in these games mirrors exactly what we do in ABA therapy—but kids think they\'re just playing.' },
  { name: 'Dr. Lisa Thompson', title: 'Speech Therapist', emoji: '👩‍⚕️', quote: 'My patients actually ASK to practice now. That\'s something I never thought I\'d see.' },
  { name: 'Dr. David Kim', title: 'Child Psychologist', emoji: '👨‍⚕️', quote: 'When speech becomes the game controller, practice stops feeling like work. It\'s brilliant design.' },
  { name: 'Dr. Rachel Green', title: 'Developmental Specialist', emoji: '👩‍⚕️', quote: 'The varied praise patterns prevent habituation—something many apps get wrong. This is clinically sound.' },
  { name: 'Dr. Andrew Martinez', title: 'SLP-CCC', emoji: '👨‍⚕️', quote: 'I recommend SpeechFormation to every family I work with. It extends therapy into the home seamlessly.' },
  { name: 'Dr. Jessica Brown', title: 'Autism Specialist', emoji: '👩‍⚕️', quote: 'For children who resist traditional therapy, play-based approaches like this are absolutely essential.' },
  { name: 'Dr. Robert Taylor', title: 'Pediatric Neurologist', emoji: '👨‍⚕️', quote: 'The progressive complexity matches developmental stages perfectly. You can tell therapists helped design this.' },
  { name: 'Dr. Amanda White', title: 'Speech-Language Pathologist', emoji: '👩‍⚕️', quote: 'Finally, an app that parents can use confidently at home without needing a therapy degree.' },
  { name: 'Dr. Kevin Lee', title: 'Clinical Psychologist', emoji: '👨‍⚕️', quote: 'Play isn\'t just fun—it\'s how children\'s brains are wired to learn. SpeechFormation leverages this beautifully.' },
];

// Therapist testimonials data moved below

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);

  useEffect(() => {
    loadPlans();
  }, []);

  // Handle responsive card count
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCards(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % therapistTestimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const loadPlans = async () => {
    try {
      const data = await plansApi.getAll();
      setPlans(data.plans || data || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };

  const getFeatures = (plan) => {
    if (plan.features) {
      if (Array.isArray(plan.features)) return plan.features;
      if (typeof plan.features === 'string') {
        try { return JSON.parse(plan.features); } catch { return []; }
      }
    }
    return [];
  };

  const nextGame = () => {
    setCurrentGameIndex((prev) => Math.min(prev + 1, featuredGames.length - visibleCards));
  };

  const prevGame = () => {
    setCurrentGameIndex((prev) => Math.max(prev - 1, 0));
  };

  return (
    <div className="min-h-screen">
      {/* ==================== SECTION A: HERO ==================== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 bg-primary-100 rounded-full text-primary-700 text-sm font-medium mb-6">
                <img src="https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696e8f2ddf5fa55637865a98.png" alt="" className="h-5 w-auto mr-2" />
                Speech practice that feels like play
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Help your child 
                <span className="text-primary-600"> find their voice</span>
                <br />through play
              </h1>
              
              <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl">
                Voice-controlled games designed by parents and therapists for children 
                with speech delays and autism. Because every child deserves to learn 
                through joy, not just therapy.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to={isAuthenticated ? "/dashboard" : "/register"} 
                  className="btn-primary text-lg px-8 py-4"
                >
                  {isAuthenticated ? "Go to Dashboard" : "Start Free Today"}
                </Link>
                <Link 
                  to="/pricing" 
                  className="btn-secondary text-lg px-8 py-4"
                >
                  See Plans
                </Link>
              </div>

              <p className="mt-4 text-sm text-gray-500">
                No credit card required • Free forever tier available
              </p>
            </div>

            <div className="relative">
              <div className="relative bg-white rounded-3xl shadow-2xl p-6 transform rotate-2 hover:rotate-0 transition-transform duration-300">
                <div className="bg-gradient-to-br from-teal-400 to-primary-500 rounded-2xl p-8 text-white text-center">
                  <div className="text-8xl mb-4 animate-bounce-slow">😺</div>
                  <div className="bg-white bg-opacity-20 rounded-xl p-4 backdrop-blur">
                    <p className="font-display text-xl">"Say OPEN to help Pip!"</p>
                  </div>
                  <div className="mt-4 flex justify-center">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <span className="text-2xl">🎤</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-yellow-400 text-yellow-900 rounded-full px-4 py-2 font-bold transform rotate-12 shadow-lg">
                Ages 3-18
              </div>
              <div className="absolute -bottom-4 -left-4 bg-green-400 text-green-900 rounded-full px-4 py-2 font-bold transform -rotate-6 shadow-lg">
                Voice Controlled!
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION B: BUILT BY A PARENT ==================== */}
      <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-b from-white via-primary-50/20 to-white">
        {/* Background image - faded, positioned left */}
        <div 
          className="absolute inset-0 bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/dad-son-left.jpg)',
            backgroundPosition: 'left center',
            backgroundSize: 'auto 100%',
            opacity: 0.22,
          }}
        />
        
        {/* Gradient overlay - fades image on the right side only */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-white" />
        
        {/* Subtle decorative blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-100/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center px-4 py-2 bg-primary-100 rounded-full text-primary-700 text-sm font-medium mb-4">
              <span className="mr-2">💙</span>
              Our Story
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              Built by a parent who gets it
            </h2>
          </div>
          
          <div className="bg-white rounded-3xl p-8 sm:p-10 lg:p-12 shadow-xl border border-gray-100 relative">
            {/* Quote accent */}
            <div className="absolute -top-5 left-10 w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>
            
            <div className="mb-8">
              <p className="text-gray-600 italic text-xl leading-relaxed">
                "After a long day at school, the last thing my son Isaac wanted was another therapy 
                session. The scripted exercises, the repetition, the clinical feel—it all felt like work, 
                not childhood."
              </p>
            </div>
            
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                I realized something: puppies don't learn to communicate through drills. They learn 
                through <strong className="text-primary-700">play</strong>. Why should our children be any different?
              </p>
              
              <p>
                So I built the games I wished existed. Games where speech isn't homework—it's 
                how you pop balloons, launch rockets, and help a hungry cat named Pip find 
                snacks.
              </p>
            </div>
            
            {/* Founder attribution - with placeholder for real photo */}
            <div className="flex items-center gap-4 mt-10 pt-8 border-t border-gray-100">
              {/* 
                PLACEHOLDER: Replace this div with a real photo of Mike & Isaac
                <img src="/images/mike-isaac.jpg" alt="Mike and Isaac" className="w-16 h-16 rounded-full object-cover shadow-md" />
              */}
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-teal-500 rounded-full flex items-center justify-center shadow-md">
                <span className="text-2xl">👨‍👦</span>
              </div>
              <div>
                <p className="font-bold text-gray-900 text-lg">Mike</p>
                <p className="text-gray-500">Founder & Isaac's Dad</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION C: GAME CARDS ==================== */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Games your child will actually want to play
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-gray-600">
              Every game targets real speech therapy goals—hidden inside genuine fun.
            </p>
          </div>

          <div className="relative">
            {/* Game Cards Grid - Responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredGames.slice(currentGameIndex, currentGameIndex + visibleCards).map((game) => (
                <div
                  key={game.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className={`h-36 sm:h-40 ${game.color} flex items-center justify-center`}>
                    <span className="text-5xl sm:text-6xl">{game.icon}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{game.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{game.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {game.skills.map((skill, i) => (
                        <span key={i} className="px-2 py-1 bg-teal-50 text-teal-700 text-xs rounded-full font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <div className="flex justify-center gap-3 mt-8">
              <button
                onClick={prevGame}
                className="w-10 h-10 bg-white rounded-full shadow flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 transition-all"
                disabled={currentGameIndex === 0}
                aria-label="Previous games"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextGame}
                className="w-10 h-10 bg-white rounded-full shadow flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 transition-all"
                disabled={currentGameIndex >= featuredGames.length - visibleCards}
                aria-label="Next games"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dot indicators for mobile */}
            <div className="flex justify-center gap-2 mt-4 sm:hidden">
              {featuredGames.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentGameIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentGameIndex ? 'bg-teal-500 w-6' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to game ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/games"
              className="inline-flex items-center px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl transition-all shadow-md"
            >
              Explore All Games
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== SECTION C2: TRY THE DEMO - LEAD CAPTURE ==================== */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#1e3a5f] via-[#1a3352] to-[#152a45] relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Gradient orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2dd4bf]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#f97316]/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              {/* Logo accent */}
              <div className="inline-flex items-center gap-3 mb-6">
                <img 
                  src="https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696e8f2ddf5fa55637865a98.png" 
                  alt="" 
                  className="h-8 w-auto"
                />
                <span className="text-[#2dd4bf] font-semibold text-sm uppercase tracking-wider">Interactive Demo</span>
              </div>
              
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Experience the journey <span className="text-[#2dd4bf]">firsthand</span>
              </h2>
              
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                See how our voice-powered platform guides learners through 10 developmental phases. 
                This interactive demo showcases the methodology trusted by therapists and loved by families.
              </p>
              
              {/* Features - clean design */}
              <div className="space-y-4 mb-10">
                {[
                  'Full 10-phase progression preview',
                  'Real-time voice recognition technology',
                  'Evidence-based speech development approach'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#2dd4bf] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-[#1e3a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-200">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to="/register?redirect=demo"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2dd4bf] hover:bg-[#5eead4] text-[#1e3a5f] font-bold text-lg rounded-xl transition-all hover:shadow-lg hover:shadow-[#2dd4bf]/20"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  Try Demo Free
                </Link>
                <Link 
                  to="/our-approach"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/20"
                >
                  Learn the Science
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              
              <p className="mt-4 text-gray-400 text-sm">
                Free account required • No credit card needed
              </p>
            </div>
            
            {/* Right: Professional Preview */}
            <div className="relative">
              {/* Main card */}
              <div className="bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
                {/* Header bar */}
                <div className="bg-white/5 px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img 
                      src="https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696e8f2ddf5fa55637865a98.png" 
                      alt="SpeechFormation" 
                      className="h-6 w-auto"
                    />
                    <span className="text-white/80 font-medium">Pip's Journey</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#2dd4bf]"></div>
                    <span className="text-[#2dd4bf] text-sm font-medium">Demo Mode</span>
                  </div>
                </div>
                
                {/* Content area */}
                <div className="p-6">
                  {/* Phase progress */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-400">Phase Progress</span>
                      <span className="text-[#2dd4bf] font-medium">10 Phases</span>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(10)].map((_, i) => (
                        <div 
                          key={i} 
                          className={`h-2 flex-1 rounded-full ${i < 3 ? 'bg-[#2dd4bf]' : 'bg-white/20'}`}
                        ></div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Demo screen mockup */}
                  <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-xl p-6 mb-6">
                    <div className="text-center">
                      <p className="text-gray-400 text-sm mb-2">Current Phase</p>
                      <h3 className="text-white text-xl font-bold mb-4">Word Combinations</h3>
                      <div className="inline-flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-white font-medium">Listening...</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">20</p>
                      <p className="text-gray-400 text-xs">Levels</p>
                    </div>
                    <div className="text-center border-x border-white/10">
                      <p className="text-2xl font-bold text-[#2dd4bf]">10</p>
                      <p className="text-gray-400 text-xs">Phases</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">All</p>
                      <p className="text-gray-400 text-xs">Ages</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating accent */}
              <div className="absolute -bottom-4 -right-4 bg-[#f97316] text-white rounded-xl px-4 py-2 font-semibold text-sm shadow-lg">
                100% Free Access
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION D: CTA BANNER ==================== */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to make speech practice fun?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Join thousands of families turning therapy fatigue into genuine joy.
          </p>
          <Link 
            to={isAuthenticated ? "/dashboard" : "/register"} 
            className="inline-flex items-center px-8 py-4 bg-white text-teal-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            Start Playing Free
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>
      {/* ==================== SECTION E: PRICING ==================== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">
              Simple, honest pricing
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Start free. Upgrade when you're ready. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free */}
            <div className="card border-2 border-gray-200">
              <div className="text-center">
                <h3 className="font-display text-xl font-bold text-gray-900">Free</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$0</span>
                  <span className="text-gray-500">/forever</span>
                </div>
                <p className="mt-2 text-gray-500">No credit card required</p>
              </div>
              <ul className="mt-6 space-y-3">
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> First 20 levels of Pip's Adventure
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> Voice recognition
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> 1 child profile
                </li>
              </ul>
              <Link to="/register" className="btn-secondary w-full mt-8">
                Get Started
              </Link>
            </div>

            {/* Basic */}
            <div className="card border-2 border-primary-500 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                Most Popular
              </div>
              <div className="text-center">
                <h3 className="font-display text-xl font-bold text-gray-900">Basic</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$10</span>
                  <span className="text-gray-500">/month</span>
                </div>
              </div>
              <ul className="mt-6 space-y-3">
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> 100 levels across games
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> Progress tracking
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> 3 child profiles
                </li>
              </ul>
              <Link to="/register" className="btn-primary w-full mt-8">
                Start Free Trial
              </Link>
            </div>

            {/* Premium */}
            <div className="card border-2 border-gray-200">
              <div className="text-center">
                <h3 className="font-display text-xl font-bold text-gray-900">Premium</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$50</span>
                  <span className="text-gray-500">/month</span>
                </div>
              </div>
              <ul className="mt-6 space-y-3">
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> All games + new releases
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> Unlimited levels
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> Therapist consultation access
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="text-primary-500 mr-2">✓</span> Unlimited child profiles
                </li>
              </ul>
              <Link to="/register" className="btn-secondary w-full mt-8">
                Get Premium
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION F: HOW IT WORKS ==================== */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Speech practice disguised as fun
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              No more "say this 10 times." Just play.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-teal-200 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <span className="text-4xl">🎈</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Voice = Game Control
              </h3>
              <p className="text-gray-600">
                Say "POP" to pop balloons. Say "JUMP" to make the ball jump. 
                Speech becomes the controller, not the homework.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-100 to-accent-200 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <span className="text-4xl">🧪</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Backed by Science
              </h3>
              <p className="text-gray-600 mb-3">
                Developed with clinical psychologists using ABA principles and 
                speech therapy best practices—hidden inside genuine gameplay.
              </p>
              <Link to="/our-approach" className="inline-flex items-center text-teal-600 font-semibold text-sm hover:text-teal-700 transition-colors">
                Learn about our methodology
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            <div className="text-center p-6">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <span className="text-4xl">📈</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Progress That Matters
              </h3>
              <p className="text-gray-600">
                Track vocabulary growth, session duration, and engagement—all 
                without your child ever feeling "tested."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION G: SCIENCE + THERAPIST CAROUSEL ==================== */}
      <section className="py-20 lg:py-28 bg-[#16477c] text-white relative overflow-hidden">
        {/* Subtle decorative elements - using brand colors */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/15 rounded-full text-sm font-medium mb-6 backdrop-blur-sm">
                <span className="mr-2">🧪</span>
                Evidence-Based Approach
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                The science of play-based learning
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Our games are built on proven therapeutic principles, developed in collaboration 
                with <strong className="text-white">Dr. Terri Smith</strong>, a clinical psychologist with 
                over 40 years in practice.
              </p>
              
              <div className="space-y-4">
                {[
                  'ABA (Applied Behavior Analysis) principles woven into gameplay',
                  'Natural prompt fading from explicit to open-ended questions',
                  'Varied praise patterns (100+ unique phrases) to prevent habituation',
                  'Progressive complexity matching developmental stages',
                ].map((point, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-teal-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-white/85">{point}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-10 flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="w-11 h-11 flex-shrink-0 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/40 backdrop-blur-sm">
                  <span className="text-sm font-semibold tracking-wide text-white">TS</span>
                </div>
                <p className="text-white/70 text-sm text-center sm:text-left">
                  Methodology shaped by <strong className="text-white">Dr. Terri Smith</strong>, clinical
                  psychologist — 40+ years of practice with children and families
                </p>
              </div>
              
              <Link 
                to="/our-approach" 
                className="mt-8 inline-flex items-center px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/30"
              >
                See Our Full Methodology
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            {/* Therapist Testimonial Carousel */}
            <div className="relative">
              <div className="bg-white rounded-3xl p-8 shadow-2xl overflow-hidden">
                <div className="text-center mb-4">
                  <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">What Our Therapists Say</p>
                </div>
                
                <div className="relative h-64 overflow-hidden">
                  {therapistTestimonials.map((therapist, i) => (
                    <div 
                      key={i} 
                      className={`absolute inset-0 flex flex-col justify-center px-4 transition-all duration-700 ease-in-out ${
                        i === currentTestimonialIndex 
                          ? 'opacity-100 translate-y-0' 
                          : 'opacity-0 translate-y-8'
                      }`}
                    >
                      <div className="text-5xl mb-4 text-center">
                        {therapist.emoji}
                      </div>
                      <p className="text-gray-700 italic text-center mb-4 text-lg leading-relaxed">
                        "{therapist.quote}"
                      </p>
                      <div className="text-center">
                        <p className="font-bold text-gray-900">{therapist.name}</p>
                        <p className="text-gray-500 text-sm">{therapist.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-center space-x-2 mt-4">
                  {therapistTestimonials.slice(0, 6).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentTestimonialIndex(i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === currentTestimonialIndex % 6 ? 'bg-[#16477c] w-6' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION H: COMMUNITY PARTNERS ==================== */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-teal-100 rounded-full text-teal-700 text-sm font-medium mb-4">
              <span className="mr-2">🤝</span>
              Join Our Community
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Be part of something meaningful
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
              Whether you're a parent, therapist, educator, or creative—there's a place for you 
              in the SpeechFormation community.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* For Parents */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <span className="text-2xl">👨‍👩‍👧</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">For Parents</h3>
              <p className="text-gray-600 text-sm mb-4">
                Turn screen time into speech practice. Help your child at home with games they'll actually want to play.
              </p>
              <Link to="/for-parents" className="inline-flex items-center text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors">
                Learn More
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            {/* For Therapists */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <span className="text-2xl">🩺</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">For Therapists</h3>
              <p className="text-gray-600 text-sm mb-4">
                Extend your impact beyond sessions. Join our network, contribute to games, and earn additional income.
              </p>
              <Link to="/for-therapists" className="inline-flex items-center text-teal-600 font-semibold text-sm hover:text-teal-700 transition-colors">
                Learn More
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            {/* For Schools */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-gradient-to-br from-accent-400 to-accent-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <span className="text-2xl">🏫</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">For Schools</h3>
              <p className="text-gray-600 text-sm mb-4">
                Bring SpeechFormation to your special education program with site licenses and IEP alignment.
              </p>
              <Link to="/for-schools" className="inline-flex items-center text-accent-600 font-semibold text-sm hover:text-accent-700 transition-colors">
                Learn More
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            {/* For Creatives */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <span className="text-2xl">🎨</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">For Creatives</h3>
              <p className="text-gray-600 text-sm mb-4">
                Turn your creativity into impact and income. Design characters, stories, and games that help kids.
              </p>
              <Link to="/for-creatives" className="inline-flex items-center text-purple-600 font-semibold text-sm hover:text-purple-700 transition-colors">
                Learn More
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION I: FINAL CTA ==================== */}
      <section className="py-16 bg-gradient-to-r from-teal-500 via-teal-600 to-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to make speech practice fun?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Join thousands of families turning therapy fatigue into genuine joy.
          </p>
          <Link 
            to={isAuthenticated ? "/dashboard" : "/register"} 
            className="inline-flex items-center px-8 py-4 bg-white text-teal-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            {isAuthenticated ? "Go to Dashboard" : "Start Playing Free"}
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}
